import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { chunkedUpload, MAX_UPLOAD_MB } from "../lib/chunkedUpload";
import { Upload, Loader2, CloudOff, UploadCloud, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { DriveFileRow } from "./drive/DriveFileRow";

export function DriveDocuments({ category, refId = "", refLabel = "", docTypes = [], dateHint = "", compact = false }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [docType, setDocType] = useState(docTypes[0] || "");
  const [customType, setCustomType] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [rescanning, setRescanning] = useState(false);
  const autoRescanned = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { category };
      if (refId) params.ref_id = refId;
      const { data } = await api.get("/drive/files", { params });
      setFiles(data);
    } catch {
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [category, refId]);

  useEffect(() => { load(); }, [load]);

  // Poll while any file is still being parsed by the AI.
  useEffect(() => {
    if (!files.some((f) => f.parse_status === "pending")) return;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [files, load]);

  // Auto re-scan any document that never read successfully (skipped/failed) — once per open.
  useEffect(() => {
    if (autoRescanned.current || loading || !refId) return;
    if (!files.some((f) => f.parse_status === "skipped" || f.parse_status === "failed")) return;
    autoRescanned.current = true;
    api.post("/drive/rescan", null, { params: { category, ref_id: refId } })
      .then((r) => { if (r.data?.count) { toast.info("Re-reading documents…"); setTimeout(load, 1500); } })
      .catch(() => {});
  }, [files, loading, refId, category, load]);

  async function rescan(f) {
    setBusyId(f.id);
    try {
      await api.post(`/drive/files/${f.id}/rescan`);
      setFiles((prev) => prev.map((x) => (x.id === f.id ? { ...x, parse_status: "pending" } : x)));
      toast.info("Re-reading document…");
      setTimeout(load, 1500);
    } catch {
      toast.error("Re-scan failed");
    } finally {
      setBusyId(null);
    }
  }

  async function rescanAll() {
    setRescanning(true);
    try {
      const r = await api.post("/drive/rescan", null, { params: { category, ref_id: refId, all: true } });
      toast.success(r.data?.count ? `Re-reading ${r.data.count} document${r.data.count > 1 ? "s" : ""}…` : "Nothing to re-scan");
      setTimeout(load, 1500);
    } catch {
      toast.error("Re-scan failed");
    } finally {
      setRescanning(false);
    }
  }

  async function doUpload(file) {
    if (!file) return;
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) { toast.error(`File must be under ${MAX_UPLOAD_MB} MB.`); return; }
    setUploading(true);
    setProgress(0);
    try {
      await chunkedUpload(file, {
        kind: "drive",
        category,
        ref_id: refId || "",
        ref_label: refLabel || "",
        doc_type: (customType.trim() || docType) || "",
        date_hint: dateHint || "",
      }, setProgress);
      toast.success("Uploaded to Google Drive");
      setCustomType("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }

  function onUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    doUpload(file);
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    const file = e.dataTransfer.files?.[0];
    doUpload(file);
  }

  async function download(f) {
    setBusyId(f.id);
    try {
      const res = await api.get(`/drive/files/${f.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: f.mime_type }));
      const a = document.createElement("a");
      a.href = url; a.download = f.name; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    } finally {
      setBusyId(null);
    }
  }

  async function remove(f) {
    if (!window.confirm(`Delete "${f.name}" from Drive?`)) return;
    setBusyId(f.id);
    try {
      await api.delete(`/drive/files/${f.id}`);
      toast.success("Deleted");
      setFiles((prev) => prev.filter((x) => x.id !== f.id));
    } catch {
      toast.error("Delete failed");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div data-testid={`drive-docs-${category}`}>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        {docTypes.length > 0 && (
          <select
            className="input-field w-40"
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            data-testid="drive-doctype-select"
          >
            {docTypes.map((d) => <option key={d}>{d}</option>)}
          </select>
        )}
        <input
          className="input-field w-48"
          placeholder="or type a custom title"
          value={customType}
          onChange={(e) => setCustomType(e.target.value)}
          data-testid="drive-custom-title"
        />
        <label className="btn-secondary cursor-pointer w-fit" data-testid="drive-upload-trigger">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? (progress > 0 ? `Uploading ${progress}%` : "Uploading...") : "Upload to Drive"}
          <input type="file" className="hidden" onChange={onUpload} disabled={uploading} data-testid="drive-upload-input" />
        </label>
        {files.length > 0 && (
          <button type="button" className="btn-ghost text-sm" onClick={rescanAll} disabled={rescanning} title="Re-read all documents with AI" data-testid="drive-rescan-all">
            <RefreshCw className={`w-4 h-4 ${rescanning ? "animate-spin" : ""}`} /> Re-scan all
          </button>
        )}
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`rounded border border-dashed p-3 transition-colors ${dragOver ? "border-[#C84B31] bg-[#FBEAE7]" : "border-[#D8D6CE]"}`}
        data-testid={`drive-dropzone-${category}`}
      >
        <div className="flex items-center justify-center gap-2 text-xs text-[#A09E96] pb-2 border-b border-[#F0EDE5] mb-2">
          <UploadCloud className="w-4 h-4" /> Drag &amp; drop a file here, or use the button above (up to {MAX_UPLOAD_MB} MB)
        </div>
        {loading ? (
          <div className="text-sm text-[#68665E] flex items-center gap-2 py-1"><Loader2 className="w-4 h-4 animate-spin" /> Loading files…</div>
        ) : files.length === 0 ? (
          <div className="text-sm text-[#A09E96] flex items-center gap-2 py-3"><CloudOff className="w-4 h-4" /> No documents uploaded yet.</div>
        ) : (
          <div className="border border-[#E5E3DB] divide-y divide-[#E5E3DB]">
            {files.map((f) => (
              <DriveFileRow key={f.id} f={f} busy={busyId === f.id} onDownload={download} onDelete={remove} onRescan={rescan} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default DriveDocuments;
